<template>
  <div class="flex flex-col gap-y-8">
    <!-- 体験タイトルと画像表示セクション -->
    <TitleWithImage
      text-style="text-[16px] md:text-[17px] font-light leading-[22px] tracking-wider"
      :img="props.thumbnailImageUrl ?? ''"
      :on-click-image="props.onClickImage"
      :slug="props.slug"
      :title="props.experienceEvent.experience.name"
    />
    <div>
      <div class="flex-raw flex justify-between gap-y-2">
        <p class="text-[22px] text-lg">{{ preferredDateTimeLabel }}</p>
        <nuxt-link class="inline-block cursor-pointer text-gold3 underline underline-offset-2" data-testid="edit-preferred-date-time-link" @click="props.onClickImage">Edit</nuxt-link>
      </div>
      <p class="text-[16px] text-beige4 md:text-[17px]">{{ firstPreferredDateTime }}</p>
    </div>
    <ReservationOptionFields :experience-event="props.experienceEvent" :get-error-message="props.getErrorMessage">
      <template #heading>
        <p class="text-[22px] text-lg">Additional Options</p>
      </template>
    </ReservationOptionFields>
    <!-- スマホのみ表示：体験タイトルと画像表示 + 予約情報表示セクション -->
    <TotalPriceSection
      v-if="!props.experienceEvent.timeSlotId"
      class="md:hidden"
      :applied-code="reservationStep2Form.code"
      :number-of-participants="reservationStep1Form.numOfParticipants"
      :pre-reservation-price="props.preReservationPrice"
      :selected-experience-event="props.experienceEvent"
      :selected-experience-event-options="reservationStep2Form.experienceEventOptions"
      :selected-interpreter-option="reservationStep2Form.experienceEventInterpreterOption"
    />
    <TotalPriceSectionWithGuestType
      v-else
      class="md:hidden"
      :applied-code="reservationStep2Form.code"
      :experience-event="props.experienceEvent"
      :pre-reservation-price-with-guest-type="props.preReservationPriceWithGuestType"
    />
  </div>
</template>

<script setup lang="ts">
import TitleWithImage from '@/wabunka-src/common/components/pure/TitleWithImage/TitleWithImage.vue';
import TotalPriceSection from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSection.vue';
import TotalPriceSectionWithGuestType from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSectionWithGuestType.vue';
import type { ExperiencePreReservationPriceViewModel, WabunkaReservationFormStep1, WabunkaReservationFormStep2 } from '@/wabunka-src/features/Experience';
import { isOvernightStay } from '@/wabunka-src/features/Experience/models/experience';
import { buildWabunkaExperienceEventPreferredDateTime, type WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';
import ReservationOptionFields from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationStep2Form/ReservationOptionFields/ReservationOptionFields.vue';

interface Props {
  /** エラーメッセージを取得する関数 */
  getErrorMessage: (s: string) => string;
  /** サムネイル画像のURL */
  thumbnailImageUrl?: string;
  /** 体験のスラッグ */
  slug: string;
  /** 体験イベントの情報 */
  experienceEvent: WabunkaExperienceEventViewModel;
  /** 予約フォームのステップ1の情報 */
  reservationStep1Form: WabunkaReservationFormStep1;
  /** 予約フォームのステップ2の情報 */
  reservationStep2Form: WabunkaReservationFormStep2;
  /** 体験料金の情報 */
  preReservationPrice: ExperiencePreReservationPriceViewModel;
  /** ゲストタイプ対応の体験料金の情報 */
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  /** 体験タイトルと画像表示セクションのクリックイベントハンドラ */
  onClickImage: () => void;
}
const props = defineProps<Props>();

const preferredDateTimeLabel = computed(() => (isOvernightStay(props.experienceEvent.experience) ? 'Preferred date and check-in time（JST）' : 'Preferred date and time（JST）'));

const firstPreferredDateTime = computed(() => buildWabunkaExperienceEventPreferredDateTime(props.experienceEvent));
</script>
